import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  FileText, Plus, Trash2, Upload, DollarSign,
  Clock, AlertTriangle, CheckCircle, Package, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ARManagement() {
  const { token } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [tab, setTab] = useState('receivables');
  const [receivables, setReceivables] = useState([]);
  const [assets, setAssets] = useState([]);
  const [summary, setSummary] = useState({});
  const [assetSummary, setAssetSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const [arForm, setArForm] = useState({
    debtor_name: '', debtor_contact: '', invoice_number: '',
    invoice_amount: '', invoice_date: '', due_date: '', description: ''
  });
  const [assetForm, setAssetForm] = useState({
    asset_type: 'equipment', description: '', estimated_value: '', acquisition_date: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [arRes, assetRes] = await Promise.all([
        axios.get(`${API}/business/receivables`, { headers }),
        axios.get(`${API}/business/assets`, { headers }),
      ]);
      setReceivables(arRes.data.receivables);
      setSummary(arRes.data.summary);
      setAssets(assetRes.data.assets);
      setAssetSummary(assetRes.data.summary);
    } catch (err) {
      toast({ title: 'Error loading data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [token]);

  const addReceivable = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/business/receivables`, {
        ...arForm,
        invoice_amount: parseFloat(arForm.invoice_amount),
      }, { headers });
      toast({ title: 'Receivable added' });
      setAddOpen(false);
      setArForm({ debtor_name: '', debtor_contact: '', invoice_number: '', invoice_amount: '', invoice_date: '', due_date: '', description: '' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const addAsset = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/business/assets`, {
        ...assetForm,
        estimated_value: parseFloat(assetForm.estimated_value),
      }, { headers });
      toast({ title: 'Asset added' });
      setAddAssetOpen(false);
      setAssetForm({ asset_type: 'equipment', description: '', estimated_value: '', acquisition_date: '' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const uploadInvoice = async (receivableId, file) => {
    setUploadingId(receivableId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/business/receivables/${receivableId}/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Invoice PDF uploaded' });
      fetchData();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setUploadingId(null);
    }
  };

  const deleteReceivable = async (id) => {
    try {
      await axios.delete(`${API}/business/receivables/${id}`, { headers });
      toast({ title: 'Receivable deleted' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Cannot delete', variant: 'destructive' });
    }
  };

  const deleteAsset = async (id) => {
    try {
      await axios.delete(`${API}/business/assets/${id}`, { headers });
      toast({ title: 'Asset deleted' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Cannot delete', variant: 'destructive' });
    }
  };

  const getAgingColor = (days) => {
    if (days === 0) return 'bg-green-500/10 text-green-400 border-green-500/20';
    if (days <= 30) return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
    if (days <= 60) return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
    return 'bg-red-500/10 text-red-400 border-red-500/20';
  };

  const getAgingLabel = (days) => {
    if (days === 0) return 'Current';
    if (days <= 30) return `${days}d overdue`;
    if (days <= 60) return `${days}d overdue`;
    return `${days}d overdue`;
  };

  return (
    <DashboardLayout title="Collateral Management">
      <div data-testid="ar-management-page">

        {/* Summary Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-sm text-gray-400">Total AR</div>
            <div className="text-xl font-bold" data-testid="total-ar-value">${(summary.total_ar_value || 0).toLocaleString()}</div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-sm text-gray-400">Available AR</div>
            <div className="text-xl font-bold text-green-400">${(summary.available_value || 0).toLocaleString()}</div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-sm text-gray-400">Total Assets</div>
            <div className="text-xl font-bold">${(assetSummary.total_value || 0).toLocaleString()}</div>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="text-sm text-gray-400">Pledged</div>
            <div className="text-xl font-bold text-orange-400">${((summary.pledged_value || 0) + (assetSummary.pledged_value || 0)).toLocaleString()}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button variant={tab === 'receivables' ? 'default' : 'outline'} size="sm"
            onClick={() => setTab('receivables')} data-testid="tab-receivables">
            <FileText className="w-4 h-4 mr-2" /> Receivables ({receivables.length})
          </Button>
          <Button variant={tab === 'assets' ? 'default' : 'outline'} size="sm"
            onClick={() => setTab('assets')} data-testid="tab-assets">
            <Package className="w-4 h-4 mr-2" /> Assets ({assets.length})
          </Button>
        </div>

        {/* Receivables Tab */}
        {tab === 'receivables' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Accounts Receivable</h2>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500" data-testid="add-receivable-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Invoice
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0a0a0a] border-white/10">
                  <DialogHeader><DialogTitle>Add Account Receivable</DialogTitle></DialogHeader>
                  <form onSubmit={addReceivable} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Customer / Debtor</Label>
                        <Input required value={arForm.debtor_name} onChange={(e) => setArForm({...arForm, debtor_name: e.target.value})}
                          className="mt-1 bg-white/5 border-white/10" placeholder="Company name" data-testid="ar-debtor-input" />
                      </div>
                      <div>
                        <Label>Invoice Number</Label>
                        <Input required value={arForm.invoice_number} onChange={(e) => setArForm({...arForm, invoice_number: e.target.value})}
                          className="mt-1 bg-white/5 border-white/10" placeholder="INV-001" data-testid="ar-invoice-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Invoice Amount ($)</Label>
                      <Input required type="number" step="0.01" min="1" value={arForm.invoice_amount}
                        onChange={(e) => setArForm({...arForm, invoice_amount: e.target.value})}
                        className="mt-1 bg-white/5 border-white/10" placeholder="50000" data-testid="ar-amount-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Invoice Date</Label>
                        <Input required type="date" value={arForm.invoice_date} onChange={(e) => setArForm({...arForm, invoice_date: e.target.value})}
                          className="mt-1 bg-white/5 border-white/10" data-testid="ar-invoice-date" />
                      </div>
                      <div>
                        <Label>Due Date</Label>
                        <Input required type="date" value={arForm.due_date} onChange={(e) => setArForm({...arForm, due_date: e.target.value})}
                          className="mt-1 bg-white/5 border-white/10" data-testid="ar-due-date" />
                      </div>
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea value={arForm.description} onChange={(e) => setArForm({...arForm, description: e.target.value})}
                        className="mt-1 bg-white/5 border-white/10" rows={2} placeholder="Services rendered..." />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-purple-500" data-testid="submit-receivable-btn">
                      Add Receivable
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Aging Summary */}
            {summary.aging && (
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { label: 'Current', count: summary.aging.current, color: 'bg-green-500/10 text-green-400' },
                  { label: '1-30 days', count: summary.aging.days_1_30, color: 'bg-yellow-500/10 text-yellow-400' },
                  { label: '31-60 days', count: summary.aging.days_31_60, color: 'bg-orange-500/10 text-orange-400' },
                  { label: '61-90 days', count: summary.aging.days_61_90, color: 'bg-red-500/10 text-red-300' },
                  { label: '90+ days', count: summary.aging.days_90_plus, color: 'bg-red-500/20 text-red-400' },
                ].map(a => (
                  <span key={a.label} className={`px-3 py-1 rounded-full text-xs font-medium ${a.color}`}>
                    {a.label}: {a.count}
                  </span>
                ))}
              </div>
            )}

            {/* Receivables List */}
            <div className="space-y-3">
              {receivables.map((r) => (
                <motion.div key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="p-4 rounded-xl bg-white/[0.03] border border-white/5"
                  data-testid={`receivable-${r.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-semibold">{r.debtor_name}</h4>
                        <span className="text-xs text-gray-500">#{r.invoice_number}</span>
                        <Badge className={getAgingColor(r.days_outstanding)}>
                          {getAgingLabel(r.days_outstanding)}
                        </Badge>
                        {r.pledged && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Pledged</Badge>}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="font-medium text-white text-lg">${r.invoice_amount.toLocaleString()}</span>
                        <span>Due: {new Date(r.due_date).toLocaleDateString()}</span>
                        {r.description && <span className="truncate max-w-[200px]">{r.description}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.document_url ? (
                        <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> PDF</span>
                      ) : (
                        <Button size="sm" variant="outline" className="text-xs border-white/10"
                          disabled={uploadingId === r.id}
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf';
                            input.onchange = (e) => {
                              if (e.target.files[0]) uploadInvoice(r.id, e.target.files[0]);
                            };
                            input.click();
                          }}
                        >
                          <Upload className="w-3 h-3 mr-1" /> {uploadingId === r.id ? 'Uploading...' : 'Upload PDF'}
                        </Button>
                      )}
                      {!r.pledged && (
                        <Button size="sm" variant="ghost" onClick={() => deleteReceivable(r.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
              {receivables.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No receivables yet. Add your first invoice to get started.</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Assets Tab */}
        {tab === 'assets' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Tangible Assets</h2>
              <Dialog open={addAssetOpen} onOpenChange={setAddAssetOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-gradient-to-r from-blue-500 to-purple-500" data-testid="add-asset-btn">
                    <Plus className="w-4 h-4 mr-1" /> Add Asset
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0a0a0a] border-white/10">
                  <DialogHeader><DialogTitle>Add Tangible Asset</DialogTitle></DialogHeader>
                  <form onSubmit={addAsset} className="space-y-3">
                    <div>
                      <Label>Asset Type</Label>
                      <Select value={assetForm.asset_type} onValueChange={(v) => setAssetForm({...assetForm, asset_type: v})}>
                        <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="asset-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="inventory">Inventory</SelectItem>
                          <SelectItem value="property">Property</SelectItem>
                          <SelectItem value="vehicle">Vehicle</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input required value={assetForm.description} onChange={(e) => setAssetForm({...assetForm, description: e.target.value})}
                        className="mt-1 bg-white/5 border-white/10" placeholder="e.g., CNC Machine, Office Building" data-testid="asset-desc-input" />
                    </div>
                    <div>
                      <Label>Estimated Value ($)</Label>
                      <Input required type="number" step="0.01" min="1" value={assetForm.estimated_value}
                        onChange={(e) => setAssetForm({...assetForm, estimated_value: e.target.value})}
                        className="mt-1 bg-white/5 border-white/10" placeholder="100000" data-testid="asset-value-input" />
                    </div>
                    <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-purple-500" data-testid="submit-asset-btn">
                      Add Asset
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {assets.map((a) => (
                <motion.div key={a.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="p-4 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-between"
                  data-testid={`asset-${a.id}`}
                >
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="font-semibold">{a.description}</h4>
                      <Badge className="bg-white/5 text-gray-400 border-white/10 capitalize">{a.asset_type}</Badge>
                      {a.pledged && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Pledged</Badge>}
                    </div>
                    <span className="text-lg font-medium">${a.estimated_value.toLocaleString()}</span>
                  </div>
                  {!a.pledged && (
                    <Button size="sm" variant="ghost" onClick={() => deleteAsset(a.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </motion.div>
              ))}
              {assets.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No assets yet. Add equipment, inventory, or property to boost your borrowing power.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
